// ============================================================
// ApiResolverPlugin — stream metadata and user data merger
// ============================================================
import type { IPlugin, PluginContext, ApiData, UserData } from '../core/types';

export class ApiResolverPlugin implements IPlugin {
  readonly name = 'api-resolver';

  private ctx: PluginContext | null = null;
  private apiData: ApiData | null = null;
  private userDataCache: Array<Partial<UserData>> = [];
  private loaded = false;
  private appendRetries = 0;
  private static readonly MAX_APPEND_RETRIES = 20;

  init(ctx: PluginContext): void {
    this.ctx = ctx;

    // If apiData pre-loaded in config, use it immediately
    if (ctx.config.apiData) {
      this.handleApiData(ctx.config.apiData);
    }

    ctx.logger.debug('ApiResolverPlugin initialized');
  }

  destroy(): void {
    this.ctx = null;
    this.apiData = null;
    this.loaded = false;
    this.userDataCache = [];
  }

  /**
   * Called by LegacyAdapter.appendUserData() to merge user data
   * If API data not yet loaded, queues updates in userDataCache
   */
  appendUserData(user: Partial<UserData>): void {
    if (!this.loaded) {
      this.userDataCache.push(user);
      return;
    }

    if (this.apiData) {
      if (!this.apiData.user) {
        this.apiData.user = { ...user } as UserData;
      } else {
        Object.assign(this.apiData.user, user);
      }
      this.ctx?.eventBus.emit('api:user', this.apiData.user);
      this.ctx?.store.setState({ user: { ...this.apiData.user } });
      this.appendRetries = 0;
    } else if (this.appendRetries < ApiResolverPlugin.MAX_APPEND_RETRIES) {
      this.appendRetries++;
      setTimeout(() => this.appendUserData(user), 50);
    } else {
      this.ctx?.logger.warn('appendUserData: retry limit exceeded, data dropped');
      this.appendRetries = 0;
    }
  }

  /**
   * Process API data from config and emit events
   */
  private handleApiData(data: ApiData): void {
    if (!this.ctx) return;

    this.apiData = data;
    this.loaded = true;

    // Apply cached user data updates
    if (this.userDataCache.length > 0) {
      if (!this.apiData.user) {
        this.apiData.user = Object.assign({} as UserData, ...this.userDataCache);
      } else {
        for (const cached of this.userDataCache) {
          Object.assign(this.apiData.user, cached);
        }
      }
      this.userDataCache = [];
    }

    // Emit events
    this.ctx.eventBus.emit('api:data', this.apiData);

    if (this.apiData.user) {
      this.ctx.eventBus.emit('api:user', this.apiData.user);
      this.ctx.store.setState({ user: { ...this.apiData.user } });
    }

    // Update store with API-derived state
    this.ctx.store.setState({
      isOnline: this.apiData.channel_status === 'online',
    });

    if (this.apiData.viewers != null) {
      this.ctx.store.setState({ viewers: this.apiData.viewers });
    }

    this.ctx.logger.debug('API data loaded:', { channelStatus: this.apiData.channel_status });
  }

}
